import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  useTheme,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import { useOutletContext } from "react-router-dom";
import StarIcon from "@mui/icons-material/Star";

const reviews = [
  {
    name: "Sarah M.",
    location: "London, UK",
    avatar: "S",
    text: "Found flights and an Airbnb for Barcelona in under 2 minutes. I used to spend hours doing this across different tabs. Genuinely won't go back.",
  },
  {
    name: "Kenji T.",
    location: "Tokyo, Japan",
    avatar: "K",
    text: "The AI review summary is something else. It flagged the wifi issues before I booked — read hundreds of reviews so I didn't have to. That alone saved the trip.",
  },
  {
    name: "Priya D.",
    location: "Sydney, Australia",
    avatar: "P",
    text: "Explore mode suggested Oaxaca and I just went for it. Honestly the best trip I've ever taken — would never have thought of it on my own.",
  },
];

const features = [
  {
    emoji: "🛫🏠",
    title: "Flights + Airbnb, Together",
    desc: "Search flights and Airbnbs in one shot. See the full trip cost at a glance.",
  },

  {
    emoji: "🎯",
    title: "Smart Filters",
    desc: "Filter by rating, review count, cancellation policy, and 90+ amenities. Only see stays worth your time.",
  },
  {
    emoji: "🧠",
    title: "Review Analyzer",
    desc: "AI reads and summarizes a listing's reviews, so you can skip the research.",
  },
  {
    emoji: "🚀",
    title: "Explore Mode",
    desc: "Not sure where to go? AI suggests destinations based on your dates and budget.",
  },

  {
    emoji: "🗺️",
    title: "Map View",
    desc: "See filtered Airbnb listings on a map to find your perfect spot.",
  },
  {
    emoji: "🌍",
    title: "Multi-Currency",
    desc: "Search and compare prices in your own currency, no mental math needed.",
  },
  {
    emoji: "🛠️",
    title: "Trip Planner AI",
    desc: "Tell us your trip and get a full day-by-day itinerary in seconds.",
  },
  {
    emoji: "🚫",
    title: "Ad-Free",
    desc: "No ads, no distractions. Just clean results.",
  },
];

const comingSoon = [
  {
    emoji: "🏨",
    title: "Hotel Options",
    desc: "Compare hotels alongside Airbnbs to find the best deal for your trip.",
  },
  {
    emoji: "📬",
    title: "Weekly Deals",
    desc: "Get the best flight + stay combos for your saved destinations, every week.",
  },
  {
    emoji: "📉",
    title: "Price Tracking",
    desc: "Watch a listing or route and get notified when the price drops.",
  },
];

const Subscribe = () => {
  const theme = useTheme();
  const { onSignUpClick, isPro } = useOutletContext();

  return (
    <Box
      id="subscribe-section"
      sx={{
        py: 0,
        px: 0,
        backgroundColor:
          theme.palette.mode === "dark" ? "rgba(0,0,0,0.2)" : "grey.100",
        textAlign: "center",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Box sx={{ py: 8, px: 2, maxWidth: "960px", mx: "auto" }}>
          {/* Heading */}
          <Typography
            component="h2"
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 1.5,
              lineHeight: { xs: 1.2, sm: 1.3 },
              fontSize: { xs: "2rem", sm: "3rem" },
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {isPro ? "Welcome back, Pro member ✈️" : "The tool that pays for itself the first time you use it"}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              maxWidth: "560px",
              mx: "auto",
              mb: 6,
              fontSize: { xs: "1rem", sm: "1.15rem" },
              lineHeight: 1.6,
              color: "text.secondary",
            }}
          >
            {isPro ? (
              "You have full access to every feature below. Go plan something great."
            ) : (
              <>
                Free to use — 5 searches a day, no card needed. Go unlimited for{" "}
                <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                  $4.99/month
                </Box>{" "}
                when you're ready.
              </>
            )}
          </Typography>

          {/* Feature cards */}
          <Grid container spacing={3} sx={{ mb: 6, justifyContent: "center" }}>
            {features.map(({ emoji, title, desc }, i) => (
              <Grid item xs={12} sm={6} md={3} key={title}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.45,
                    delay: i * 0.07,
                    ease: "easeOut",
                  }}
                  style={{ height: "100%" }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      borderRadius: "20px",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.5,
                      position: "relative",
                      overflow: "hidden",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "rgba(255,255,255,0.75)",
                      backdropFilter: "blur(12px)",
                      border: `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.07)"
                      }`,
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 4px 24px rgba(0,0,0,0.3)"
                          : "0 4px 24px rgba(0,0,0,0.06)",
                      transition: "transform 0.25s ease, box-shadow 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? `0 12px 40px rgba(${theme.palette.primary.main
                                .replace("#", "")
                                .match(/.{2}/g)
                                .map((h) => parseInt(h, 16))
                                .join(",")}, 0.25)`
                            : "0 12px 40px rgba(0,0,0,0.12)",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        borderRadius: "20px 20px 0 0",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.7rem",
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}22, ${theme.palette.secondary.main}22)`,
                        border: `1px solid ${theme.palette.primary.main}33`,
                        mt: 0.5,
                      }}
                    >
                      {emoji}
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      lineHeight={1.3}
                    >
                      {title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      lineHeight={1.6}
                    >
                      {desc}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* CTA */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mb: 8 }}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              {isPro ? (
                <Button
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  variant="contained"
                  size="large"
                  sx={{
                    fontWeight: 800,
                    fontSize: "1.1rem",
                    py: 1.8,
                    px: 5,
                    color: "#fff",
                    borderRadius: "50px",
                    textTransform: "none",
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                    boxShadow: "0 10px 30px rgba(95, 90, 162, 0.4)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px) scale(1.02)",
                      boxShadow: "0 15px 35px rgba(95, 90, 162, 0.5)",
                    },
                  }}
                >
                  Search Now ✈️
                </Button>
              ) : (
                <>
                  <Button
                    onClick={onSignUpClick}
                    variant="contained"
                    size="large"
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.1rem",
                      py: 1.8,
                      px: 5,
                      color: "#fff",
                      borderRadius: "50px",
                      textTransform: "none",
                      background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                      boxShadow: "0 10px 30px rgba(95, 90, 162, 0.4)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px) scale(1.02)",
                        boxShadow: "0 15px 35px rgba(95, 90, 162, 0.5)",
                      },
                    }}
                  >
                    Sign Up
                  </Button>
                  <Button
                    onClick={() => {
                      const el = document.getElementById("search-form");
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "center" });
                      } else {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                    variant="outlined"
                    size="large"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      py: 1.8,
                      px: 5,
                      borderRadius: "50px",
                      textTransform: "none",
                      border: `2px solid ${theme.palette.primary.main}`,
                      color: theme.palette.primary.main,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px) scale(1.02)",
                        background: `${theme.palette.primary.main}11`,
                        borderColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.main,
                        boxShadow: `0 8px 24px ${theme.palette.primary.main}44`,
                      },
                    }}
                  >
                    Search Now
                  </Button>
                </>
              )}
            </Box>
            {!isPro && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.78rem", letterSpacing: 0.2 }}>
                Free plan available — no credit card needed to start.
              </Typography>
            )}
          </Box>

          {/* Coming soon cards */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}
            >
              Coming soon
            </Typography>
            <Grid container spacing={3} sx={{ mt: 0.5, justifyContent: "center" }}>
              {comingSoon.map(({ emoji, title, desc }) => (
                <Grid item xs={12} sm={6} md={3} key={title}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      borderRadius: "20px",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.5,
                      position: "relative",
                      overflow: "hidden",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.02)"
                          : "rgba(255,255,255,0.5)",
                      border: `1px dashed ${
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0,0,0,0.1)"
                      }`,
                      opacity: 0.7,
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.7rem",
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}11, ${theme.palette.secondary.main}11)`,
                        border: `1px solid ${theme.palette.primary.main}22`,
                        mt: 0.5,
                      }}
                    >
                      {emoji}
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
                      {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                      {desc}
                    </Typography>
                    <Chip label="Coming soon" size="small" sx={{ borderRadius: "8px", mt: "auto" }} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* Social proof — tinted band */}
        <Box
          sx={{
            mt: 2,
            py: 6,
            background:
              theme.palette.mode === "dark"
                ? "rgba(0,0,0,0.25)"
                : "rgba(0,0,0,0.03)",
            borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4, px: 2 }}>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                mb: 0.5,
                fontSize: { xs: "1.6rem", sm: "2rem" },
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              What travelers are saying
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Real people. Real trips. No incentive to say nice things.
            </Typography>
          </Box>

          {/* Horizontal scroll row */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              pb: 1,
              px: 3,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              justifyContent: { sm: "center" },
              flexWrap: { xs: "nowrap", md: "wrap" },
            }}
          >
            {reviews.map(({ name, location, avatar, text }, i) => (
              <Box
                key={name}
                sx={{
                  minWidth: { xs: "270px", sm: "280px" },
                  maxWidth: "300px",
                  flexShrink: 0,
                  p: 2.5,
                  borderRadius: "16px",
                  textAlign: "left",
                  background:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "#fff",
                  border: `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.07)"
                  }`,
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "none"
                      : "0 2px 12px rgba(0,0,0,0.06)",
                }}
              >
                <Box sx={{ display: "flex", gap: 0.3, mb: 1.5 }}>
                  {[...Array(5)].map((_, j) => (
                    <StarIcon key={j} sx={{ color: "#f59e0b", fontSize: "0.85rem" }} />
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65, fontSize: "0.875rem" }}>
                  "{text}"
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      color: "#fff",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      flexShrink: 0,
                    }}
                  >
                    {avatar}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} fontSize="0.82rem" lineHeight={1.2}>
                      {name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                      {location}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

export default Subscribe;
